<template>
  <div class="py-4 container-fluid">
    <div class="row mb-3">
      <div class="col-12 d-flex flex-wrap justify-content-between align-items-center">
        <div>
          <h5 class="mb-0">{{ $t('reports.warehouseTitle') }}</h5>
          <p class="mb-0 text-sm text-secondary">{{ $t('reports.warehouseSubtitle') }}</p>
        </div>
        <material-button variant="outline" color="dark" size="sm" :disabled="!rows.length" @click="onExport">
          <i class="material-icons text-sm me-1">download</i>{{ $t('reports.exportCsv') }}
        </material-button>
      </div>
    </div>

    <!-- Filters -->
    <div class="card mb-3">
      <div class="card-body d-flex flex-wrap gap-3 align-items-end">
        <div class="warehouse-filter-date">
          <label class="text-xs text-secondary d-block mb-1">{{ $t('reports.warehousePeriod') }}</label>
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            value-format="YYYY-MM-DD"
            format="DD/MM/YYYY"
            :clearable="false"
            :editable="false"
            :shortcuts="shortcuts"
            :disabled-date="disabledDate"
            placement="bottom-start"
            popper-class="warehouse-date-popper"
            @calendar-change="onCalendarChange"
            @visible-change="rangeStart = null"
            @change="onFilterChange"
          />
        </div>
        <div>
          <label class="text-xs text-secondary d-block mb-1">{{ $t('reports.warehouseKind') }}</label>
          <el-select v-model="kind" style="width: 160px" @change="onFilterChange">
            <el-option :label="$t('reports.warehouseAllKinds')" value="" />
            <el-option :label="$t('reports.warehouseKindInternal')" value="Internal" />
            <el-option :label="$t('reports.warehouseKindRent')" value="Rent" />
            <el-option :label="$t('reports.warehouseKindLoan')" value="Loan" />
          </el-select>
        </div>
        <div class="flex-grow-1" style="max-width: 320px">
          <label class="text-xs text-secondary d-block mb-1">{{ $t('reports.warehouseSearch') }}</label>
          <el-input
            v-model="searchString"
            class="search-input"
            clearable
            prefix-icon="Search"
            :placeholder="$t('reports.warehouseSearchPlaceholder')"
            @input="onSearchInput"
          />
        </div>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-secondary" role="status"></div>
    </div>

    <template v-else>
      <!-- KPI cards -->
      <div class="row">
        <div class="col-xl-3 col-sm-6 mb-4">
          <div class="card"><div class="card-body p-3">
            <p class="text-xs text-uppercase text-secondary mb-1">{{ $t('reports.warehouseKindInternal') }}</p>
            <h5 class="mb-0 font-weight-bolder">{{ internalCount }}</h5>
          </div></div>
        </div>
        <div class="col-xl-3 col-sm-6 mb-4">
          <div class="card"><div class="card-body p-3">
            <p class="text-xs text-uppercase text-secondary mb-1">{{ $t('reports.warehouseKindRent') }}</p>
            <h5 class="mb-0 font-weight-bolder">{{ rentCount }}</h5>
          </div></div>
        </div>
        <div class="col-xl-3 col-sm-6 mb-4">
          <div class="card"><div class="card-body p-3">
            <p class="text-xs text-uppercase text-secondary mb-1">{{ $t('reports.warehouseKindLoan') }}</p>
            <h5 class="mb-0 font-weight-bolder">{{ loanCount }}</h5>
          </div></div>
        </div>
        <div class="col-xl-3 col-sm-6 mb-4">
          <div class="card"><div class="card-body p-3">
            <p class="text-xs text-uppercase text-secondary mb-1">{{ $t('reports.warehouseStillOut') }}</p>
            <h5 class="mb-0 font-weight-bolder">{{ stillOutCount }}</h5>
          </div></div>
        </div>
      </div>

      <div class="card">
        <div class="card-body">
          <!-- Mobile: compact card list instead of a horizontally scrolling table -->
          <div class="d-md-none">
            <div v-for="row in rows" :key="row.kind + '-' + row.reservationId" class="border-bottom py-2">
              <div class="d-flex justify-content-between align-items-center">
                <router-link :to="{ name: 'Tool', params: { toolId: row.toolId } }" class="text-dark font-weight-bold text-sm">
                  {{ row.toolName }}
                  <span class="text-xs text-secondary">#{{ row.customerToolId }}</span>
                  <span v-if="row.quantity && row.quantity > 1" class="text-xs text-secondary">(x{{ row.quantity }})</span>
                </router-link>
                <span class="badge ms-2" :class="kindBadgeClass(row.kind)">{{ kindLabel(row.kind) }}</span>
              </div>
              <div class="text-sm">
                {{ row.reservedBy }}<template v-if="projectLabel(row)"> — {{ projectLabel(row) }}</template>
              </div>
              <div class="text-xs text-secondary">
                {{ formatDateTime(row.reservedTimestamp) }}
                <span v-if="row.isReserved" class="badge bg-gradient-secondary ms-1">{{ $t('reports.warehouseStillOut') }}</span>
                <template v-else> → {{ formatDateTime(row.unreservedTimestamp) }}</template>
              </div>
            </div>
            <div v-if="!rows.length" class="text-center text-secondary text-sm py-4">{{ $t('reports.noData') }}</div>
          </div>

          <el-table :data="rows" stripe class="d-none d-md-block">
            <el-table-column :label="$t('reports.tool')" min-width="170">
              <template #default="{ row }">
                <router-link :to="{ name: 'Tool', params: { toolId: row.toolId } }" class="text-dark font-weight-bold">
                  {{ row.toolName }}
                </router-link>
                <span class="text-xs text-secondary"> #{{ row.customerToolId }}</span>
                <span v-if="row.quantity && row.quantity > 1" class="text-xs text-secondary"> (x{{ row.quantity }})</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('reports.warehouseKind')" align="center" min-width="100">
              <template #default="{ row }">
                <span class="badge" :class="kindBadgeClass(row.kind)">{{ kindLabel(row.kind) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="reservedBy" :label="$t('reports.reservedBy')" min-width="140" />
            <el-table-column :label="$t('common.project')" min-width="150">
              <template #default="{ row }">{{ projectLabel(row) }}</template>
            </el-table-column>
            <el-table-column :label="$t('reports.warehouseTakenAt')" min-width="130">
              <template #default="{ row }">{{ formatDateTime(row.reservedTimestamp) }}</template>
            </el-table-column>
            <el-table-column :label="$t('reports.warehouseReturnedAt')" min-width="130">
              <template #default="{ row }">
                <span v-if="row.isReserved" class="badge bg-gradient-secondary">{{ $t('reports.warehouseStillOut') }}</span>
                <template v-else>{{ formatDateTime(row.unreservedTimestamp) }}</template>
              </template>
            </el-table-column>
          </el-table>
          <div class="d-flex justify-content-end mt-3">
            <el-pagination v-model:current-page="currentPage" :page-size="pageSize" :total="totalItems" layout="prev, pager, next" @current-change="fetchData" />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import api from "../../services/api";
import MaterialButton from "@/components/MaterialButton.vue";
import { exportCsv } from "../../utils/csvExport";

const MAX_RANGE_DAYS = 31;

export default {
  name: "WarehouseOverview",
  components: { MaterialButton },
  data() {
    const today = this.toIso(new Date());
    return {
      dateRange: [today, today],
      kind: "",
      searchString: "",
      searchTimer: null,
      rangeStart: null,
      rows: [],
      totalItems: 0,
      internalCount: 0,
      rentCount: 0,
      loanCount: 0,
      stillOutCount: 0,
      currentPage: 1,
      pageSize: 20,
      loading: true,
    };
  },
  computed: {
    shortcuts() {
      const day = (offset) => {
        const d = new Date();
        d.setDate(d.getDate() + offset);
        return d;
      };
      return [
        { text: this.$t("reports.warehouseToday"), value: () => [day(0), day(0)] },
        { text: this.$t("reports.warehouseYesterday"), value: () => [day(-1), day(-1)] },
        { text: this.$t("reports.warehouseLast7Days"), value: () => [day(-6), day(0)] },
        {
          text: this.$t("reports.warehouseThisMonth"),
          value: () => {
            const now = new Date();
            return [new Date(now.getFullYear(), now.getMonth(), 1), now];
          },
        },
      ];
    },
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    toIso(d) {
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    },
    formatDateTime(v) {
      return v ? new Date(v).toLocaleString("is-IS", { dateStyle: "short", timeStyle: "short" }) : "";
    },
    kindLabel(kind) {
      if (kind === "Rent") return this.$t("reports.warehouseKindRent");
      if (kind === "Loan") return this.$t("reports.warehouseKindLoan");
      return this.$t("reports.warehouseKindInternal");
    },
    kindBadgeClass(kind) {
      if (kind === "Rent") return "bg-gradient-success";
      if (kind === "Loan") return "bg-gradient-warning";
      return "bg-gradient-info";
    },
    projectLabel(row) {
      return (row.projectKey ? "[" + row.projectKey + "] " : "") + (row.projectName || "");
    },
    // Limit the picker to a one-month span (mirrors the backend validation)
    onCalendarChange(dates) {
      this.rangeStart = dates && dates[0] ? dates[0] : null;
    },
    disabledDate(date) {
      if (!this.rangeStart) return false;
      return Math.abs(date - this.rangeStart) > MAX_RANGE_DAYS * 24 * 3600 * 1000;
    },
    onFilterChange() {
      this.currentPage = 1;
      this.fetchData();
    },
    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.onFilterChange, 400);
    },
    async fetchData() {
      this.loading = true;
      try {
        const params = {
          fromDate: this.dateRange[0],
          toDate: this.dateRange[1],
          currentPage: this.currentPage,
          pageSize: this.pageSize,
        };
        if (this.kind) params.kind = this.kind;
        if (this.searchString) params.searchString = this.searchString;
        const response = await api.get("/Analytics/WarehouseCheckouts", { params });
        this.rows = response.data.result || [];
        this.totalItems = response.data.totalItems || 0;
        this.internalCount = response.data.internalCount || 0;
        this.rentCount = response.data.rentCount || 0;
        this.loanCount = response.data.loanCount || 0;
        this.stillOutCount = response.data.stillOutCount || 0;
      } catch (e) {
        console.error("WarehouseOverview fetchData error:", e); // eslint-disable-line no-console
        this.$toast.error(this.$t("common.unableToRetrieveData"), { timeout: 0 });
      } finally {
        this.loading = false;
      }
    },
    onExport() {
      const columns = [
        { key: "toolName", label: this.$t("reports.tool") },
        { key: "kindText", label: this.$t("reports.warehouseKind") },
        { key: "reservedBy", label: this.$t("reports.reservedBy") },
        { key: "projectText", label: this.$t("common.project") },
        { key: "reservedTimestamp", label: this.$t("reports.warehouseTakenAt") },
        { key: "unreservedTimestamp", label: this.$t("reports.warehouseReturnedAt") },
      ];
      const rows = this.rows.map((r) => ({
        ...r,
        kindText: this.kindLabel(r.kind),
        projectText: this.projectLabel(r),
      }));
      exportCsv(`warehouse-checkouts-${this.dateRange[0]}_${this.dateRange[1]}`, columns, rows);
    },
  },
};
</script>

<style>
/* Date range picker popper — ensure it stacks above the sidebar */
.warehouse-date-popper.el-popper {
  z-index: 9999 !important;
}

/* Mobile: keep the popup inside the viewport — shrink the shortcut sidebar and
   stack the two month panels vertically instead of side by side */
@media (max-width: 767.98px) {
  /* Keep the range input inside the filter card */
  .warehouse-filter-date {
    width: 100%;
  }
  .warehouse-filter-date .el-date-editor {
    width: 100% !important;
  }
  .warehouse-date-popper.el-popper {
    max-width: calc(100vw - 16px) !important;
    width: auto !important;
  }
  .warehouse-date-popper .el-date-range-picker,
  .warehouse-date-popper .el-date-range-picker.has-sidebar {
    width: 100% !important;
  }
  .warehouse-date-popper .el-picker-panel__sidebar {
    width: 90px !important;
  }
  .warehouse-date-popper .el-picker-panel__body {
    margin-left: 90px !important;
    min-width: auto !important;
  }
  .warehouse-date-popper .el-date-range-picker__content {
    display: block !important;
    width: 100% !important;
    padding: 8px !important;
  }
  .warehouse-date-popper .el-date-range-picker__content.is-left {
    border-right: none !important;
    border-bottom: 1px solid var(--el-datepicker-inner-border-color);
  }
  .warehouse-date-popper .el-picker-panel__shortcut {
    padding-left: 8px !important;
    font-size: 12px !important;
  }
}
</style>
